// Copyright (c) 2016 Deere & Company

import './pin-cross-reference-report.scss';
import React, {
    Component,
    PropTypes
} from 'react';
import Criteria from '../state/criteria';
import FavoriteIcon from '../../../shared/components/favorites/favorite-icon';
import {FormattedMessage} from 'react-intl';
import Formatting from '../state/formatting';
import GeneralSettings from '@deere/cps-it.warranty-model-factory/lib/general-settings';
import NumberOfRecordsFound from '../../../shared/components/number-of-records-found/index';
import PinCrossReference from '@deere/cps-it.warranty-model-factory/lib/pin-cross-reference';
import PinCrossReferenceReportCriteria from './pin-cross-reference-report-criteria';
import ReportGrid from '../../shared/components/report-grid';
import ReportHeader from '../../shared/components/report-header';
import ReportHiddenColumns from '../../shared/components/report-columns';
import ReportLayout from '@deere/cps-it.warranty-model-factory/lib/report-layout';
import ShowNoResultsFound from '../../shared/components/show-no-results-found/show-no-results-found';
import View from '../state/view';
import WarrantyLoadingIndicator from '../../../shared/components/warranty-loading-indicator';
import {exportToCSV} from '../../../reports/shared/services/export-to-csv';
import {getAgGridLocaleText} from '../../../shared/utils/ag-grid-locale-text';
import {getDefaultReportLayout} from '../../shared/services/report-variant';
import {getPinCrossReferenceColumnDefinitions} from '../report-services/column-definitions';
import {scrollToAndResizeReport} from '../../shared/services/scroll-window';

function handleProductPinChange(pin) {

    if (pin) {

        this.actions.updateCriteriaValidity(true);

    } else {

        this.actions.updateCriteriaValidity(false);

    }

    this.actions.updatePinCrossReferenceCriteria('pin', pin);

}

function handleCreatePinCrossReferenceReport() {

    this.actions.toggleReportIsScrollable();
    this.actions.resetSearchResults();
    this.actions.createPinCrossReferenceReport(this.criteria);

}

function handleGridApiDefinition(params) {

    let defaultColumnState,
        defaultSortModel;

    this.actions.createAgGridApi(params.api, params.columnApi);

    if (this.selectedLayout && this.selectedLayout.name && !this.isInitialLoad) {

        const defaultLayout = this.layouts.find(layout => !layout.name);

        defaultColumnState = defaultLayout.columnState;
        defaultSortModel = defaultLayout.sortModel;

    } else {

        defaultColumnState = params.columnApi.getColumnState();
        defaultSortModel = params.api.getSortModel();

        this.actions.updateReportProperty('isInitialLoad', false);

    }

    this.actions.updateDefaultLayout(defaultColumnState, defaultSortModel);

    if (this.selectedLayout && this.selectedLayout.name) {

        params.columnApi.setColumnState(this.selectedLayout.columnState);
        params.api.setSortModel(this.selectedLayout.sortModel);
        params.api.setFilterModel(this.selectedLayout.filterModel);

    } else {

        const defaultLayout = getDefaultReportLayout(defaultColumnState, defaultSortModel);

        this.actions.updateReportProperty('selectedLayout', defaultLayout);

    }

}

function onColumnRemoved(columnEvent) {

    if (!columnEvent.visible) {

        this.actions.hideReportColumn(this.columnApi.getColumnState());

    }

}

function handleExportToCsv() {

    const fileName = 'pin-cross-reference';

    return exportToCSV(fileName, this.gridApi);

}

function handleCriteriaReset() {

    this.actions.updatePinCrossReferenceCriteria('pin', '');
    this.actions.resetSearchResults();
    this.actions.updateCriteriaValidity(false);
    this.actions.updateShowNoResultsFound(false);

}

class PinCrossReferenceReport extends Component {

    componentWillMount() {

        this.props.actions.getReportLayouts('pin-cross-reference');

    }

    componentDidUpdate(prevProps) {

        if (this.props.reportIsScrollable && this.props.report.length) {

            this.props.actions.resetReportIsScrollable();

            scrollToAndResizeReport();

        }

        const isFirstRun = !prevProps.report.length && !prevProps.showNoResultsFound;
        const layoutWasChanged = this.props.selectedLayout !== prevProps.selectedLayout;
        const readyToApplyLayout = this.props.selectedLayout && Object.keys(this.props.columnApi).length && this.props.isDefaultColumnStateAvailable;

        if ((isFirstRun || layoutWasChanged) && readyToApplyLayout) {

            this.props.columnApi.setColumnState(this.props.selectedLayout.columnState);
            this.props.gridApi.setSortModel(this.props.selectedLayout.sortModel);
            this.props.gridApi.setFilterModel(this.props.selectedLayout.filterModel);

        }

    }

    componentWillUnmount() {

        this.props.actions.resetSearchResults();
        this.props.actions.updateCriteriaValidity(false);
        this.props.actions.updateShowNoResultsFound(false);
        this.props.actions.resetReportVariant();

    }

    render() {

        return (
            <section className='pin-cross-reference'>
                <h2 className='pin-cross-reference-title'>
                    <FormattedMessage id='PIN_CROSS_REFERENCE'/>
                    <FavoriteIcon
                        id='PIN_CROSS_REFERENCE'
                        route={this.props.pathName}
                    />
                </h2>
                <PinCrossReferenceReportCriteria
                    canExport={this.props.report.length > 0}
                    canRunReport={this.props.isCriteriaValid}
                    criteria={this.props.criteria}
                    onCreatePinCrossReferenceReport={this.props::handleCreatePinCrossReferenceReport}
                    onCriteriaReset={this.props::handleCriteriaReset}
                    onExportToCsv={this.props::handleExportToCsv}
                    onProductPinChange={this.props::handleProductPinChange}
                />
                <WarrantyLoadingIndicator isLoading={this.props.busyCounter > 0}/>
                {this.props.report.length ?
                    <div>
                        <NumberOfRecordsFound count={this.props.report.length}/>
                        <section className='report'>
                            {this.props.isReportColumnsVisible && <ReportHiddenColumns/>}
                            <ReportHeader
                                columnApi={this.props.columnApi}
                                gridApi={this.props.gridApi}
                                reportName='pin-cross-reference'
                                showExport={false}
                            />
                            <ReportGrid
                                columnDefs={getPinCrossReferenceColumnDefinitions()}
                                localeText={getAgGridLocaleText()}
                                onColumnVisible={this.props::onColumnRemoved}
                                onGridReady={this.props::handleGridApiDefinition}
                                rowData={this.props.report}
                            />
                        </section>
                    </div> :
                    <ShowNoResultsFound showNoResultsFound={this.props.showNoResultsFound}/>
                }
            </section>
        );

    }

}

PinCrossReferenceReport.displayName = 'PinCrossReferenceReport';
PinCrossReferenceReport.propTypes = {
    actions: PropTypes.object.isRequired,
    busyCounter: PropTypes.number.isRequired,
    columnApi: PropTypes.object.isRequired,
    criteria: PropTypes.instanceOf(Criteria).isRequired,
    formatting: PropTypes.instanceOf(Formatting).isRequired,
    generalSettings: PropTypes.instanceOf(GeneralSettings).isRequired,
    gridApi: PropTypes.object.isRequired,
    isCriteriaValid: PropTypes.bool.isRequired,
    isDefaultColumnStateAvailable: PropTypes.bool.isRequired,
    isInitialLoad: PropTypes.bool.isRequired,
    isReportColumnsVisible: PropTypes.bool.isRequired,
    layouts: PropTypes.arrayOf(PropTypes.instanceOf(ReportLayout)).isRequired,
    pathName: PropTypes.string.isRequired,
    report: PropTypes.arrayOf(PropTypes.instanceOf(PinCrossReference)),
    reportIsScrollable: PropTypes.bool.isRequired,
    selectedLayout: PropTypes.instanceOf(ReportLayout),
    showNoResultsFound: PropTypes.bool.isRequired,
    views: PropTypes.arrayOf(PropTypes.instanceOf(View)).isRequired
};

export default PinCrossReferenceReport;



// WEBPACK FOOTER //
// ./src/reports/pin-cross-reference/components/pin-cross-reference-report.js